import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EastIcon from "@mui/icons-material/East";
import {
  buildOrderItems,
  calculateAmountToPay,
  calculateGarrariTotalAmountWithoutGST,
  calculateShippingCost,
  calculateTotalAmountWithOutGST,
  calculateTotalGST,
} from "../../utils/utils";
import ccLogo from "../../img/banners/Coupon-CC-Logo.png";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { Button, TextField, useMediaQuery } from "@mui/material";
import "react-circular-progressbar/dist/styles.css";
import { useTheme } from "@mui/material/styles";
import { validCouponForUser } from "../../features/cartPage/walletAPI";
import { FormattedNumber } from "../../utils/numberAnimation";
import { useSelector } from "react-redux";
import CashfreePaymentModal from "../payment/CashfreePaymentModal";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      props.isExpanded ? (
        <RemoveIcon style={{ fontSize: "18px", color: "black" }} />
      ) : (
        <AddIcon style={{ fontSize: "18px", color: "black" }} />
      )
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "transparent",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(0deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
  padding: "0 5px",
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: "700",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PaymentAccordion = ({
  userDetail,
  walletAmount,
  useWalletAmount,
  setUseWalletAmount,
  errors,
  setErrors,
  couponList,
  validCoupon,
  setValidCoupon,
  selectedCoupon,
  setSelectedCoupon,
  cart,
  freeShipping,
  selectedAddress,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState("panel1");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const [walletInput, setWalletInput] = useState();
  const [couponInput, setCouponInput] = useState("");
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);

  const handleCheckCoupon = async () => {
    const foundCoupon = couponList?.find(
      (coupon) => coupon.code === couponInput
    );
    if (foundCoupon) {
      const pass = await checkValidCoupon();
      if (pass) {
        setValidCoupon(foundCoupon);
        setErrors((prevErrors) => ({
          ...prevErrors,
          Coupon: "",
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          Coupon: "Already used twice(max. limit = 2)",
        }));
      }
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        Coupon: "Coupon code is invalid or expired",
      }));
    }
  };

  const checkValidCoupon = async () => {
    let couponData = {
      userID: userDetail?.UserID,
      coupon: couponInput,
    };

    try {
      const response = await validCouponForUser(couponData);
      if (response && response?.data && response?.data?.message) {
        // console.log(response?.data.message);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      // console.error("Error validating coupon:", error);
      return false;
    }
  };

  const handleApplyCoupon = (code) => {
    if (selectedCoupon?.couponCode === code) {
      setSelectedCoupon({
        couponCode: "",
        couponDiscountPercentage: 0,
        couponDiscountTaken: 0,
      });
    } else {
      const amount =
        parseInt(calculateTotalAmountWithOutGST(cart)) +
        parseInt(calculateTotalGST(cart)) -
        parseInt(calculateGarrariTotalAmountWithoutGST(cart));
      const save = (amount * validCoupon?.discount_percentage) / 100;

      setSelectedCoupon({
        couponCode: validCoupon?.code,
        couponDiscountPercentage: validCoupon?.discount_percentage,
        couponDiscountTaken: parseInt(save),
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        Coupon: "",
      }));
    }
  };

  const handleSave = (discount) => {
    const amount =
      parseInt(calculateTotalAmountWithOutGST(cart)) +
      calculateTotalGST(cart) -
      parseInt(calculateGarrariTotalAmountWithoutGST(cart));
    // console.log(discount, amount);
    return (amount * discount) / 100;
  };

  //   Update Wallet use input
  const handleWalletInputChange = (event) => {
    const inputValue = parseInt(event.target.value);

    // Ensure inputValue is a valid number
    if (!isNaN(inputValue)) {
      if (
        inputValue > walletAmount ||
        inputValue >
          parseInt(
            calculateAmountToPay(
              cart,
              selectedCoupon?.couponDiscountTaken,
              useWalletAmount,
              setUseWalletAmount,
              isDoorToDoorChecked,
              freeShipping,
              selectedCoupon
            )
          )
      ) {
        return;
      } else {
        setWalletInput(inputValue);
      }
    } else {
      setWalletInput(0);
    }
  };
  //   using wallet amount
  const handleWalletUse = () => {
    if (isNaN(walletInput)) {
      return;
    } else {
      setExpanded(null);

      setUseWalletAmount(parseInt(walletInput));
    }
  };

  const isDoorToDoorChecked = useSelector(
    (state) => state.priceRange.isDoorToDoorChecked
  );

  const handleProceedToPayment = () => {
    const totalAmount = calculateAmountToPay(
      cart,
      selectedCoupon?.couponDiscountTaken,
      useWalletAmount,
      setUseWalletAmount,
      isDoorToDoorChecked,
      freeShipping,
      selectedCoupon
    );
    setPaymentAmount(totalAmount);
    setOpenPaymentModal(true);
  };

  const { dealerItems, mainItems } = buildOrderItems(
    cart,
    useWalletAmount,
    selectedCoupon,
    isDoorToDoorChecked,
    freeShipping
  );

  // merge all items (dealer + main)
  const allItems = [...dealerItems, ...mainItems];

  return (
    <div className="w-full flex flex-col p-5 px-0 max-sm:px-5 lg:px-[10vw] sm:py-0">
      <div className="my-1 bg-white-1 shadow-md rounded-md p-1">
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChange("panel2")}
          className="shadow-md rounded-md"
        >
          <AccordionSummary
            aria-controls="panel2d-content"
            id="panel2d-header"
            isExpanded={expanded === "panel2"}
            sx={{
              height: "50px",
            }}
          >
            <Typography className="w-full flex items-center justify-between">
              <span className="text-[14px] text-black-1">Add Coupon</span>
              {parseInt(selectedCoupon?.couponDiscountTaken) > 0 && (
                <span className="text-[11.5px] text-black-1">
                  You saved&nbsp;
                  <b className="text-peach">
                    <FormattedNumber
                      value={parseInt(selectedCoupon?.couponDiscountTaken)}
                    />
                  </b>
                </span>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              <div className="w-full flex-col">
                <div className="w-full h-[38px] border-2 border-black-1/10 rounded-md flex items-center justify-between py-2 px-2 relative">
                  <TextField
                    type="text"
                    variant="outlined"
                    placeholder="CCNEW"
                    className="border-none outline-none w-[80%] text-[14px] font-medium text-black-1/80"
                    value={couponInput}
                    fullWidth
                    onChange={(e) => {
                      const newValue = e.target.value
                        .replace(/\s/g, "")
                        .toUpperCase();
                      setCouponInput(newValue);
                    }}
                    error={errors["Coupon"]}
                    helperText={errors["Coupon"]}
                    sx={{
                      border: "none",
                      outline: "none",
                      width: "80%",
                      height: "34px",
                      fontSize: "14px",
                      fontWeight: "500",
                      padding: "0",
                      color: "rgba(0,0,0,0.8)",
                      "& .MuiOutlinedInput-root": {
                        fontSize: "14px",
                        padding: "6px",
                        height: "34px",
                        border: "none",
                        outline: "none",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                      "& input": {
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "rgba(0, 0, 0, 0.8)",
                        border: "none",
                        outline: "none",
                        padding: "0",
                      },
                      "&:hover": {
                        outline: "none",
                      },
                    }}
                  />
                  <span
                    className="text-[14px] text-peach font-medium cursor-pointer"
                    onClick={handleCheckCoupon}
                  >
                    Check
                  </span>
                </div>
                <div className="w-full min-h-[150px] flex flex-col py-3">
                  {couponList &&
                    couponList?.map((data) => {
                      if (data?.code === validCoupon?.code) {
                        return (
                          <div className="w-full flex flex-col relative my-1">
                            <div
                              className={`w-full h-auto flex border-[2px] p-3 rounded-md relative ${
                                selectedCoupon?.couponCode === data?.code
                                  ? "border-green"
                                  : "border-black-1/10"
                              }`}
                            >
                              <div className="w-[35px] h-full mr-2">
                                <LazyLoadImage
                                  src={ccLogo}
                                  alt="cc-logo"
                                  className="w-[30px] h-[30px] object-contain"
                                  effect="blur"
                                  wrapperProps={{
                                    style: { transitionDelay: "1s" },
                                  }}
                                />
                              </div>
                              <div className="w-[calc(100%-35px)] h-full flex items-center justify-between">
                                <div className="w-[calc(100%-90px)] h-full flex flex-col">
                                  <span className="text-[15px] text-black-1 font-bold tracking-wide">
                                    {data?.code}
                                  </span>
                                  <span className="text-[11px] text-black-1/70 font-medium tracking-wide">
                                    Expires: <br />{" "}
                                    <b className="text-[12px] text-black-1 font-bold">
                                      23.02.2025
                                    </b>
                                  </span>
                                  <span className="text-[10px] text-green font-semibold tracking-wide">
                                    Terms & Conditions Apply
                                  </span>
                                </div>
                                <div className="w-[90px] h-full flex flex-col items-center justify-center">
                                  <span className="text-[12px] text-black-1 font-semibold mb-[2px]">
                                    Save&nbsp;
                                    <b>
                                      <FormattedNumber
                                        value={parseInt(
                                          handleSave(data?.discount_percentage)
                                        )}
                                      />
                                    </b>
                                  </span>
                                  <Button
                                    variant="contained"
                                    sx={{
                                      height: "20px",
                                      fontSize: "10px",
                                      fontWeight: "500",
                                      background:
                                        selectedCoupon?.couponCode ===
                                        data?.code
                                          ? "#28a447"
                                          : "#283248",
                                      textTransform: "none",
                                      fontFamily:
                                        'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                      padding: "5px",
                                      borderRadius: "4px",
                                      letterSpacing: "1px",
                                      color: "white",
                                      "&:hover": {
                                        background:
                                          selectedCoupon?.couponCode ===
                                          data?.code
                                            ? "#28a447"
                                            : "#283248",
                                      },
                                    }}
                                    onClick={() =>
                                      handleApplyCoupon(data?.code)
                                    }
                                  >
                                    {selectedCoupon?.couponCode === data?.code
                                      ? "Applied"
                                      : "Apply"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                            {errors[data?.code] && (
                              <span className="absolute font-sans top-[110%] left-0 text-peach font-medium text-[10px]">
                                {errors[data?.code]}
                              </span>
                            )}
                            <span className="text-peach text-[12px] mt-1">
                              <b className="text-peach">Note: </b>Coupon code is
                              not eligible for Bikes.
                            </span>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })}
                </div>
                <div className="w-full flex items-center justify-between pt-2 border-t-2 border-black-1/10">
                  <div className="flex flex-col">
                    <span className="text-[14px] text-black-1 font-semibold">
                      Maximum Savings:
                    </span>
                    <span className="text-[13px] text-black-1/90 font-bold">
                      <FormattedNumber
                        value={parseInt(selectedCoupon?.couponDiscountTaken)}
                      />
                    </span>
                  </div>
                  <Button
                    variant="contained"
                    sx={{
                      width: "35%",
                      height: "32px",
                      background: "#283248",
                      borderRadius: "30px",
                      fontSize: "13px",
                      fontWeight: "500",
                      letterSpacing: "0.5px",
                      color: "white",
                      padding: "5px 10px",
                      cursor: "pointer",
                      textTransform: "none",
                      "&:hover": {
                        background: "#283248",
                      },
                    }}
                    onClick={() => setExpanded(null)}
                  >
                    Save Now
                  </Button>
                </div>
              </div>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>

      <div className="my-1 bg-white-1 shadow-md rounded-md p-1">
        <Accordion
          expanded={expanded === "panel3"}
          onChange={handleChange("panel3")}
          className="shadow-md rounded-md"
        >
          <AccordionSummary
            aria-controls="panel3d-content"
            id="panel3d-header"
            isExpanded={expanded === "panel3"}
            sx={{
              height: "50px",
            }}
          >
            <Typography className="w-full flex items-center justify-between">
              <span className="text-[14px] text-black-1">Wallet</span>
              <div className="flex flex-col items-end justify-center">
                <span className="text-[11px] text-black-1">
                  Balance <br />
                </span>
                <span className="text-[13px] text-peach tracking-wide">
                  <FormattedNumber value={parseInt(walletAmount)} />
                </span>
              </div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              <div className="w-full flex-col">
                <div className="w-full  rounded-md flex items-center justify-between pb-2 px-2 relative">
                  <TextField
                    type="text"
                    variant="outlined"
                    placeholder="Enter Amount to pay from wallet"
                    value={walletInput}
                    onChange={handleWalletInputChange}
                    fullWidth
                    InputProps={{
                      inputProps: {
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      },
                    }}
                    onKeyPress={(event) => {
                      if (!/^\d$/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "14px",
                        padding: "6px",
                        height: "34px",
                        "& fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.1)",
                        },
                      },
                      "& input": {
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "rgba(0, 0, 0, 0.8)",
                      },
                      "&:hover": {
                        outline: "none",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      height: "34px",
                      background: "#283248",
                      borderRadius: "4px",
                      fontSize: "13px",
                      fontWeight: "500",
                      letterSpacing: "0.5px",
                      color: "white",
                      padding: "5px 10px",
                      cursor: "pointer",
                      textTransform: "none",
                      marginLeft: "8px",
                      "&:hover": {
                        background: "#283248",
                      },
                    }}
                    onClick={handleWalletUse}
                  >
                    Pay
                  </Button>
                </div>
              </div>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>

      {!isMobile && (
        <div className=" h-[50px] flex items-center justify-between my-1 bg-white-1 shadow-md rounded-md p-3">
          <span className="text-[13.5px] sm:text-[15px] text-peach font-semibold tracking-wide">
            Total Amount:{" "}
            <FormattedNumber
              value={parseInt(
                calculateAmountToPay(
                  cart,
                  selectedCoupon?.couponDiscountTaken,
                  useWalletAmount,
                  setUseWalletAmount,
                  isDoorToDoorChecked,
                  freeShipping,
                  selectedCoupon
                )
              )}
            />
          </span>
          <Button
            variant="contained"
            sx={{
              width: "auto",
              height: "32px",
              background: "#283248",
              borderRadius: "30px",
              fontSize: "13px",
              fontWeight: "500",
              letterSpacing: "0.5px",
              color: "white",
              padding: "5px 10px",
              cursor: "pointer",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "#283248",
              },
            }}
            onClick={handleProceedToPayment}
          >
            Proceed to Payment{" "}
            <EastIcon
              className="text-white-1 ml-1"
              style={{ fontSize: "16px" }}
            />
          </Button>
        </div>
      )}
      {/* Cashfree Modal */}
      <CashfreePaymentModal
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        userID={userDetail && userDetail?.UserID && userDetail?.UserID}
        amount={parseInt(paymentAmount)}
        shippingID={selectedAddress && selectedAddress}
        coupon={selectedCoupon}
        walletAmount={useWalletAmount}
        shippingAmount={calculateShippingCost(cart)}
        credit={null}
        items={allItems}
        cart={cart}
        selectedCoupon={selectedCoupon}
        useWalletAmount={useWalletAmount}
        setUseWalletAmount={setUseWalletAmount}
        isDoorToDoorChecked={isDoorToDoorChecked}
        freeShipping={freeShipping}
        userDetail={userDetail}
      />
    </div>
  );
};

export default PaymentAccordion;
